<template>
  <div>
    <h1>{{ userType.text }} Login</h1>
    <Permissions
      v-if="ctx.checkAppResult.checkedPermissions != null"
      :ctx="ctx"
      @accepted="accept"
      @refused="refuse"
    />

    <!--v-model below may be replaced by v-if-->
    <v-dialog
      v-model="mfaActivated"
      persistent
      width="600">
      <v-card>
        <v-card-title
          class="headline grey lighten-2"
        >MFA verification</v-card-title
        >
        <v-text-field
          id="mfaCode"
          v-model="mfaCode"
          :rules="[rules.required]"
          class="ma-3"
          label="MFA code"
        />
        <v-card-actions>
          <v-spacer />
          <v-btn
            @click="
              ctx.user.mfaToken = '';
              mfaCode = '';
            "
          >Cancel</v-btn
          >
          <v-btn @click="handleMFA()">Ok</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-form
      ref="form"
      v-model="validForm"
      @submit.prevent>
      <v-text-field
        id="usernameOrEmail"
        v-model="email"
        :rules="[rules.required]"
        :label="localization['Email address'][ctx.language]"
      />
      <v-text-field
        v-show="false"
        id="usernameOrEmail"
        v-model="ctx.user.username"
        :rules="[rules.required]"
        :label="localization['Email address'][ctx.language]"
      />
      <Password
        v-model="password"
        :label="localization['Password'][ctx.language]"
        :language="ctx.language"
        disablePasswordValidation
      />

      <v-btn
        id="submitButton"
        :disabled="!validForm || submitting"
        @click="submit"
      >{{ localization['Sign In'][ctx.language] }}</v-btn
      >

      <v-btn @click="refuse">{{ localization['Cancel'][ctx.language] }}</v-btn>

      <div v-if="false && serviceInfos.support">
        Feel free to reach our
        <a
          :href="serviceInfos.support"
          target="_blank"> helpdesk</a>
        if you have questions.
      </div>
    </v-form>

    <v-divider class="mt-3 mb-2" />

    <router-link
      :to="{ name: 'RegisterUser' }"
    ><h3>{{ localization['Create an account'][ctx.language] }}</h3></router-link>
    <br>
    <router-link
      :to="{ name: 'ResetPassword' }"
    ><h3>{{ localization['Forgot password'][ctx.language] }}</h3></router-link>
    <br>
    <router-link
      :to="{ name: 'ChangePassword' }"
    ><h3>{{ localization['Change password'][ctx.language] }}</h3></router-link>

    <Alerts :errorMsg="error" />
  </div>
</template>

<script>
import Password from './bits/Password.vue';
import Permissions from './bits/Permissions.vue';
import Alerts from './bits/Alerts.vue';
import Context from '../../context.js';
import controllerFactory from '../controller/controller.js';
import setupCalls from '../../setupcalls.json';
import userTypes from '../../userTypes.json';
import localization from '../../translationDict.json';

export default {
  components: {
    Password,
    Permissions,
    Alerts,
  },
  data: () => ({
    password: '',
    personalToken: '',
    mfaToken: '',
    mfaCode: '',
    error: '',
    checkedPermissions: null,
    checkAppResult: null,
    serviceInfos: {},
    submitting: false,
    c: null,
    ctx: {},
    validForm: false,
    email: '',
  }),
  computed: {
    mfaActivated: {
      get: function () {
        return this.ctx.user.mfaToken !== '';
      },
    },
    userType: function () {
      return (userTypes[this.$route.query.type] !== undefined) ? userTypes[this.$route.query.type] : userTypes['patient'];
    },
    rules: function () {
      return {
        required: (value) => !!value || this.localization['This field is required.'][this.ctx.language],
        email: value => /.+@.+/.test(value) || this.localization['E-mail must be valid.'][this.ctx.language],
      };
    },
  },
  watch: {
    email (newEmail) {
      this.ctx.user.username = newEmail;
    },
  },
  async created () {
    // deep clone
    this.localization = JSON.parse(JSON.stringify(localization));
    this.setupCalls = JSON.parse(JSON.stringify(setupCalls));
    this.ctx = new Context(this.$route.query);
    await this.ctx.init();
    this.c = controllerFactory(this.ctx);
    this.c
      .getServiceInfo()
      .then(this.showInfos)
      .catch(this.showError);
  },
  methods: {
    async submit () {
      this.ctx.user.username = this.ctx.user.username.trim().toLowerCase();
      try {
        await this.ctx.pryvService.getUsernameForEmail(this.ctx.user.username).then(async (uname) => {
          // check for valid username based on type
          if (this.userType.id === 'patient' && !uname.startsWith('p-')) {
            throw new Error('Must log in as patient type user');
          }
          if (this.userType.id === 'doctor' && !uname.startsWith('d-')) {
            throw new Error('Must log in as doctor type user');
          }
          if (this.userType.id === 'nushu_x_user' && !uname.startsWith('x-')) {
            throw new Error('Must log in as NushuX type user');
          }
          if (this.userType.id === 'analysis' &&
          (uname.startsWith('p-') || uname.startsWith('d-') || uname.startsWith('x-'))) {
            throw new Error('Must log in as analysis type user');
          }
        });
      } catch (err) {
        this.showError(err);
        return;
      }

      if (this.$refs.form.validate()) {
        this.submitting = true;
        try {
          await this.c.login(this.password)
            .then(async (connection) => {
              let calls = this.setupCalls[this.userType.id];
              // use the connection that is opened by `login` to issue a batch
              // call of all the stream creations.
              try {
                connection.api(calls);
              } catch (err) {
                this.showError(err);
              }
            });
          if (!this.ctx.accessState) {
            throw new Error('Context access state not defined. Verify that you are performing an Auth request process and either "poll" is specified in query parameters.');
          }
          if (!this.mfaActivated) {
            await this.c.checkAccess(this.showPermissions);
          }
        } catch (err) {
          this.showError(err);
        } finally {
          this.submitting = false;
        }
      }
    },
    // Handle provided MFA code
    async handleMFA () {
      try {
        await this.c.mfaVerify(this.mfaCode);
        await this.c.checkAccess(this.showPermissions);
      } catch (err) {
        this.showError(err);
      } finally {
        this.ctx.user.mfaToken = '';
      }
    },
    // Print requested permissions to the user
    showPermissions () {
      this.checkAppResult = this.ctx.checkAppResult;
      this.checkedPermissions = this.ctx.checkedPermissions;
    },
    // The user accepts the requested permissions
    accept () {
      this.c.acceptAccess().catch(this.showError);
    },
    // The user refuses the requested permissions
    refuse () {
      this.c.refuseAccess().catch(this.showError);
    },
    showError (error) {
      this.error = error.msg || error.message;
    },
    showInfos (infos) {
      this.serviceInfos = infos;
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/views/Authorization.vue